import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';

// Track modal count for z-index stacking
let modalCount = 0;

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
}: ModalProps) {
  const [zIndex, setZIndex] = useState(50);

  useEffect(() => {
    if (isOpen) {
      modalCount++;
      setZIndex(50 + modalCount * 10);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      if (isOpen) {
        modalCount--;
        if (modalCount === 0) {
          document.body.style.overflow = 'unset';
        }
      }
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
  };

  const modalContent = (
    <div className="fixed inset-0 overflow-y-auto" style={{ zIndex }}>
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
          style={{ zIndex }}
        />
        {/* Never taller than the viewport: the title bar stays put and the body
            scrolls, instead of the whole sheet scrolling its title, close
            button and footer off screen. */}
        <div
          role="dialog"
          aria-modal="true"
          aria-label={title}
          className={clsx(
            'relative flex max-h-[calc(100vh-2rem)] w-full flex-col bg-white dark:bg-gray-800 rounded-lg shadow-xl',
            sizeClasses[size]
          )}
          style={{ zIndex: zIndex + 1 }}
        >
          {title && (
            <div className="flex-none px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 dark:text-gray-500"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          )}
          <div className="co-modal-body min-h-0 flex-1 overflow-y-auto px-6 py-4">{children}</div>
        </div>
      </div>
    </div>
  );

  // Use portal to render at document body level
  return createPortal(modalContent, document.body);
}
